import { type FunctionComponent, useEffect, useState } from 'react';
import { type GroupContentFieldsFragment } from '../../__generated__/graphql-client-types';
import { verifyGroupsHaveContent } from '../../helpers/category-helper/category.helper';
import {
	areAllItemFacetsInRequest,
	convertConstructFacetsToFacetFilters,
	findIsSelected,
	getNonCompatibleFacets
} from '../../helpers/content-helper/content.helper';
import { requestFacetsToSelectedFacetGroups } from '../../helpers/search-helper/search-helper';
import { useSearchResults } from '../../hooks/apollo/search/search.hooks';
import { useNavigation } from '../../hooks/navigation/navigation.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { type GroupContentItem } from '../../types/content.types';
import { ClickableElement } from '../buttons';
import { ResponsiveTab, ResponsiveTabGroup } from '../common-components/responsive-tabs/responsive-tabs.component';
import { ContentContainer } from '../content-container/content-container.component';
import { CloudinaryImage } from '../images/cloudinary-image/cloudinary-image.component';
import { themePrimaryNs } from './group-content.css';

/**
 * TODO: currently this renders a generic card (similar to category card), when
 * we have component for all card types we need to selectively render based on
 * card type
 *
 * @see https://jira.impdir.com/browse/SODEV-29269
 */
const GroupItem: FunctionComponent<{ item: GroupContentItem }> = ({ item }) => {
	const [isSelected, setIsSelected] = useState(false);
	const { addFacets, removeFacets, request, results, previousResults, clearAndAddFacets, loading } = useSearchResults();
	const navigate = useNavigation();
	const { pathname } = useRouterLocation();
	const renderResults = loading ? previousResults : results;
	const facetGroups = renderResults?.facetGroups;
	let selectedFacetGroups = renderResults?.selectedFacetGroups;
	if (loading) {
		selectedFacetGroups = requestFacetsToSelectedFacetGroups(request.facetFilter, facetGroups) ?? selectedFacetGroups;
	}

	useEffect(() => {
		// During loading, check request.facetFilter directly to update selection immediately and prevent border flicker
		const selected = loading
			? areAllItemFacetsInRequest(item.facets, request.facetFilter)
			: findIsSelected(item.facets, selectedFacetGroups);

		setIsSelected(selected);
	}, [item, selectedFacetGroups, loading, request.facetFilter]);

	const handleClick = () => {
		if (item.url) {
			const parsedUrl = new URL(item.url, 'https://www.build.com');
			if (parsedUrl.pathname !== pathname) {
				navigate(item.url);
				return;
			}
		}

		const facets = convertConstructFacetsToFacetFilters(item.facets);
		if (isSelected) {
			setIsSelected(false);
			removeFacets(facets);
		} else {
			setIsSelected(true);
			const nonCompatibleFacetsArray = getNonCompatibleFacets(item, facetGroups, selectedFacetGroups);
			if (nonCompatibleFacetsArray.length === 0) {
				addFacets(facets);
			} else {
				clearAndAddFacets(facets);
			}
		}
	};

	return (
		<ClickableElement ariaLabel={`${item.title} shop-by`} onClick={handleClick} className="dib w-100-ns">
			<>
				<div className={`${isSelected && 'ba bw1 b--theme-primary'} ma1 mr3 shadow-1-ns h4 tc`} data-testid="groupItemImageWrapper">
					{item.image && (
						<CloudinaryImage
							publicID={item.image.id}
							description={item.image.description}
							options={{ width: 140, height: 140 }}
							useDimensions={false}
							className="h-100"
						/>
					)}
				</div>
				<div className="tc fw6-ns w4 w-auto-ns">{item.title}</div>
			</>
		</ClickableElement>
	);
};

type ResponsiveGroupsProps = Pick<GroupContentProps, 'groups'>;

const ResponsiveGroups: FunctionComponent<ResponsiveGroupsProps> = ({ groups }) => {
	const [firstGroup] = groups;
	return (
		<ResponsiveTabGroup startingTabName={firstGroup.title} tabListClasses="bg-theme-white bb b--theme-grey-light">
			{groups.map(({ title, items }, index) => (
				<ResponsiveTab
					name={title}
					key={title}
					tabClasses={`w-100 pa3 pa2-ns bt br bl b--theme-grey-light flex justify-between items-center f6 f7-ns mw4-ns tc fw6 br--top-ns br2-ns mr1`}
					activeTabClasses={`${themePrimaryNs} bg-theme-white`}
					inactiveTabClasses="bg-theme-grey-light-ns"
					analyticsHook={`${title}~${index}`}>
					<div className="b--theme-grey-light bt-ns br bl pa2 flex overflow-x-auto">
						{items.map((item) => (
							<GroupItem item={item} key={item.title} />
						))}
					</div>
				</ResponsiveTab>
			))}
		</ResponsiveTabGroup>
	);
};

export type GroupContentProps = {
	contentId: string;
	groups: GroupContentFieldsFragment[];
};

export const GroupContent: FunctionComponent<GroupContentProps> = ({ contentId, groups }) => {
	return verifyGroupsHaveContent(groups) ? (
		<ContentContainer contentId={contentId}>
			<ResponsiveGroups groups={groups} />
		</ContentContainer>
	) : null;
};
